import React, { useState, useEffect } from "react";
import api from "../../../services/AxiosConnection";
import { FaTimes, FaSave, FaPencilAlt, FaPlus, FaTrash } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { Vortex } from "react-loader-spinner";

const UserEntryForm = ({ user, onChange, isEditing, errors, onDelete, isSuperAdmin, showDelete, index }) => {
  const hasData = user.User_Name || user.Designation || user.Mobile_No || user.Email;

  return (
    <div className="bg-white p-4 rounded-xl shadow-md mb-3 border border-gray-100 hover:shadow-lg transition-all duration-200 relative">
      <div className="absolute top-0 left-0 bg-blue-600 text-white rounded-br-lg rounded-tl-lg px-3 py-1 text-xs font-semibold shadow-sm">
        {index + 1}
      </div>
      {isEditing && isSuperAdmin && showDelete && (
        <button
          onClick={() => onDelete(user.ID || user.tempId)}
          className="absolute top-2 right-2 p-1.5 text-red-600 hover:text-red-700 transition-colors duration-150"
          title={hasData ? "Delete User" : "Remove User"}
        >
          {hasData ? <FaTrash size={14} /> : <FaTimes size={14} />}
        </button>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-8">
        <div className="space-y-1">
          <label className="block text-gray-600 font-semibold text-xs uppercase tracking-wide">
            Name:
          </label>
          <input
            type="text"
            value={user.User_Name || ""}
            onChange={(e) => onChange("User_Name", e.target.value)}
            disabled={!isEditing || !isSuperAdmin}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-gray-600 font-semibold text-xs uppercase tracking-wide">
            Role/Designation:
          </label>
          <input
            type="text"
            value={user.Designation || ""}
            onChange={(e) => onChange("Designation", e.target.value)}
            disabled={!isEditing || !isSuperAdmin}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-gray-600 font-semibold text-xs uppercase tracking-wide">
            Phone Number:
          </label>
          <input
            type="text"
            value={user.Mobile_No || ""}
            onChange={(e) => onChange("Mobile_No", e.target.value)}
            disabled={!isEditing || !isSuperAdmin}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400"
          />
          {errors[user.tempId]?.Mobile_No && (
            <p className="text-red-500 text-xs mt-1 animate-fadeIn">
              {errors[user.tempId].Mobile_No}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="block text-gray-600 font-semibold text-xs uppercase tracking-wide">
            Email:
          </label>
          <input
            type="email"
            value={user.Email || ""}
            onChange={(e) => onChange("Email", e.target.value)}
            disabled={!isEditing || !isSuperAdmin}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400"
          />
          {errors[user.tempId]?.Email && (
            <p className="text-red-500 text-xs mt-1 animate-fadeIn">
              {errors[user.tempId].Email}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const AlarmConfigForm = () => {
  const { user, selectedSite } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";
  const [users, setUsers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsers([]);
      setLoading(true);

      try {
        if (selectedSite) {
          const response = await api.get(
            `/control-panel/getAlarmUserDetails?siteDb=${selectedSite}`
          );

          if (response.data && response.data.data.length > 0) {
            setUsers(
              response.data.data.map((user) => ({ ...user, isExisting: true }))
            );
            setIsEditing(false);
          } else if (isSuperAdmin) {
            setUsers([{ tempId: Date.now(), isNew: true }]);
            setIsEditing(true);
          } else {
            setUsers([]);
            setIsEditing(false);
          }
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        if (isSuperAdmin) {
          setUsers([{ tempId: Date.now(), isNew: true }]);
          setIsEditing(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [selectedSite, isSuperAdmin]);

  const validateFields = () => {
    const newErrors = {};
    let isValid = true;
    users.forEach((user) => {
      const userErrors = {};

      if (!user.User_Name?.trim()) {
        userErrors.User_Name = "Name is required";
        isValid = false;
      }

      if (!user.Designation?.trim()) {
        userErrors.Designation = "Designation is required";
        isValid = false;
      }
      if (!/^\d{10}$/.test(user.Mobile_No)) {
        userErrors.Mobile_No = "Invalid phone number";
        isValid = false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.Email)) {
        userErrors.Email = "Invalid email format";
        isValid = false;
      }
      if (Object.keys(userErrors).length > 0) {
        newErrors[user.tempId || user.ID] = userErrors;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!isSuperAdmin) return;
    if (!validateFields()) return;

    setLoading(true);
    try {
      const existingUsers = users.filter((user) => user.isExisting);
      const newUsers = users.filter((user) => user.isNew);

      await Promise.all(
        existingUsers.map((user) =>
          api.put("/control-panel/updateAlarmUserDetails", {
            ...user,
            siteDb: selectedSite,
          })
        )
      );

      await Promise.all(
        newUsers.map((user) =>
          api.post("/control-panel/insertAlarmUserDetails", {
            ...user,
            siteDb: selectedSite,
          })
        )
      );

      setIsEditing(false);
      const response = await api.get(
        `/control-panel/getAlarmUserDetails?siteDb=${selectedSite}`
      );
      setUsers(
        response.data.data.map((user) => ({ ...user, isExisting: true }))
      );
    } catch (error) {
      console.error("Error saving users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    if (!isSuperAdmin) return;
    setUsers([...users, { tempId: Date.now(), isNew: true }]);
    setIsEditing(true);
  };

  const handleUserChange = (userId, field, value) => {
    if (!isSuperAdmin) return;
    setUsers(
      users.map((user) =>
        user.tempId === userId || user.ID === userId
          ? { ...user, [field]: value }
          : user
      )
    );
  };

  const handleDeleteUser = async (userId) => {
    if (!isSuperAdmin) return;
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this user?"
    );
    if (!confirmDelete) return;

    setLoading(true);
    try {
      const userToDelete = users.find(
        (user) => user.ID === userId || user.tempId === userId
      );

      if (userToDelete?.isExisting && userToDelete.ID) {
        await api.delete("/control-panel/deleteAlarmUserDetails", {
          data: { siteDb: selectedSite, ID: userToDelete.ID },
        });
      }

      const updatedUsers = users.filter((user) => user.ID !== userId && user.tempId !== userId);
      setUsers(updatedUsers.length === 0 && isSuperAdmin ? [{ tempId: Date.now(), isNew: true }] : updatedUsers);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold text-blue-900 mb-1.5">
          Alert Recipient Management
        </h2>
        <p className="text-xs text-gray-600 leading-relaxed">
          {users.some((u) => u.isExisting)
            ? "Manage users receiving system alerts"
            : isSuperAdmin
            ? "Add users to receive system alerts"
            : "No users configured to receive system alerts"}
        </p>
      </div>
      {users.length === 0 && !isSuperAdmin ? (
        <div className="text-center text-gray-500 text-sm py-4 bg-white rounded-xl shadow-sm">
          No alert recipients available.
        </div>
      ) : (
        users.map((user, index) => (
          <UserEntryForm
            key={user.ID || user.tempId}
            user={user}
            onChange={(field, value) =>
              handleUserChange(user.ID || user.tempId, field, value)
            }
            isEditing={isEditing && isSuperAdmin}
            errors={errors}
            onDelete={handleDeleteUser}
            isSuperAdmin={isSuperAdmin}
            showDelete={user.isExisting || (user.isNew && index > 0)}
            index={index}
          />
        ))
      )}
      {isSuperAdmin && (
        <div className="flex items-center justify-center mt-6 pt-3 border-t border-gray-200 gap-3">
          <button
            onClick={handleAddUser}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors duration-150 flex items-center gap-1.5 hover:bg-blue-700"
          >
            <FaPlus size={14} />
            Add More Users
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium transition-colors duration-150 flex items-center gap-1.5 hover:bg-green-700"
            >
              <FaSave size={14} />
              Save Changes
            </button>
          )}
          {!isEditing && users.some((u) => u.isExisting) && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-blue-600 hover:text-blue-700 rounded-lg text-sm font-medium transition-colors duration-150 flex items-center gap-1.5"
            >
              <FaPencilAlt size={14} />
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AlarmConfigForm;